// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { Box, ButtonProps, CircularProgress } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { AsyncButton } from '../../components/Button/AsyncButton';
import { RejectDialog } from '../../components/Dialog/RejectDialog';
import PartyDisplay from '../../components/PartyDisplay';
import { StateCell, Status } from '../../components/StateCell';
import { DataTable } from '../../components/Table/DataTable';
import { useIsOperator } from '../../contexts/ModuleContext';
import { fmt } from '../../utils/common';
import { PageWrapper } from '../PageWrapper';
import { CommercialAgreementRevokeDialog } from './CommercialAgreementRevokeDialog';
import { CommercialAgreementTopupDialog } from './CommercialAgreementTopupDialog';
import { CreateCommercialAgreementDialog } from './CreateCommercialAgreementDialog';
import { ModifyCommercialAgreementDialog } from './ModifyCommercialAgreementDialog';
import { useCreateCommercialAgreementActions } from './useCreateCommercialAgreementActions';
import { useCreateCommercialAgreementRows } from './useCreateCommercialAgreementRows';

interface Payload {
  operator: string;
  user: string;
  feeReceiver: string;
  rewardReceiver?: string;
  utilityFees: string;
  depositedAmount?: string;
  accruedFeesCc?: string;
  ratePerDay?: string;
  billingPeriodMinutes?: string;
  baseFeelastBilledAt?: string;
  baseFeeState?: string;
  credentialFeeLastBilledAt?: string;
  credentialFeeState?: string;
}
type Row = {
  contractId: string;
  state: string;
  payload: Payload;
  reason?: string;
};
type Action = {
  name: string;
  action: (r: Row) => Promise<unknown>;
  buttonProps?: ButtonProps;
};
export const CommercialAgreements: React.FC = () => {
  const { rows, isLoading } = useCreateCommercialAgreementRows();
  const isOperator = useIsOperator();
  const [open, setOpen] = React.useState(false);
  const onClose = () => {
    setOpen(false);
  };
  const onOpen = () => {
    setOpen(true);
  };

  const createHeaders: (createActions: (r: Row) => Action[]) => GridColDef<Row>[] = (
    createActions,
  ) => {
    const renderActionCell = (params: GridRenderCellParams<Row>) => {
      const actions = createActions(params.row);
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          {actions.map(({ name, action, buttonProps }) => (
            <AsyncButton
              variant={buttonProps?.variant}
              color={buttonProps?.color}
              key={name}
              sx={{ mr: 1 }}
              onClick={() => action(params.row)}
            >
              {name}
            </AsyncButton>
          ))}
        </Box>
      );
    };

    return [
      {
        field: 'State',
        headerName: 'State',
        renderCell: (params) => (
          <StateCell state={params.row.state as Status} reason={params.row.reason} />
        ),
        flex: 40,
        filterable: true,
        valueGetter: (params) => params.row.state,
      },
      {
        field: 'Operator',
        headerName: 'Operator',
        flex: 40,
        filterable: true,
        valueGetter: (params) => params.row.payload.operator,
        renderCell: (r) => <PartyDisplay party={r.row.payload.operator} />,
      },
      {
        field: 'User',
        flex: 40,
        valueGetter: (params) => params.row.payload.user,
        filterable: true,
        renderCell: (r) => <PartyDisplay party={r.row.payload.user} />,
      },
      {
        field: 'Fee Receiver',
        flex: 40,
        filterable: true,
        valueGetter: (params) => params.row.payload.feeReceiver,
        renderCell: (r) => <PartyDisplay party={r.row.payload.feeReceiver} />,
      },
      {
        field: 'Reward Receiver',
        flex: 40,
        renderCell: (r) => <PartyDisplay party={r.row.payload.rewardReceiver ?? ''} />,
      },
      {
        field: 'Credential Billing Fee',
        flex: 50,
        type: 'number',
        valueGetter: (r) => Number(r.row.payload.utilityFees),
        renderCell: (r) => `${fmt(r.row.payload.utilityFees, 2)} USD`,
        filterable: true,
      },
      {
        field: 'Fee per day',
        flex: 30,
        filterable: true,
        renderCell: (r) =>
          r.row.payload.ratePerDay ? `${fmt(r.row.payload.ratePerDay, 2)} USD` : null,
      },
      {
        field: 'Billing period (minutes)',
        flex: 50,
        renderCell: (r) =>
          r.row.payload?.billingPeriodMinutes
            ? `${fmt(r.row?.payload.billingPeriodMinutes, 2)}`
            : null,
      },
      {
        field: 'Locked amount',
        flex: 40,
        valueGetter: (r) =>
          r.row.payload.depositedAmount ? Number(r.row.payload.depositedAmount) : 0,
        renderCell: (r) =>
          r.row.payload.depositedAmount ? `${fmt(r.row.payload.depositedAmount, 2)} CC` : null,
      },
      {
        field: 'Accrued fees',
        flex: 40,
        valueGetter: (r) => (r.row.payload.accruedFeesCc ? Number(r.row.payload.accruedFeesCc) : 0),
        renderCell: (r) =>
          r.row.payload.accruedFeesCc ? `${fmt(r.row.payload.accruedFeesCc, 2)} CC` : null,
      },
      {
        field: 'Base Fee - Last billed at',
        flex: 70,
        renderCell: (r) => {
          const isNew = r.row.payload.baseFeeState === 'New';
          if (isNew) {
            return null;
          }
          return r.row.payload.baseFeelastBilledAt
            ? new Date(r.row.payload.baseFeelastBilledAt).toLocaleString()
            : null;
        },
      },
      {
        field: 'Base Fee Status',
        flex: 70,
        renderCell: (r) => r.row.payload.baseFeeState,
      },
      {
        field: 'Credential Fee - Last billed at',
        flex: 70,
        renderCell: (r) => {
          const isNew = r.row.payload.credentialFeeState === 'New';
          if (isNew) {
            return null;
          }
          return r.row.payload.credentialFeeLastBilledAt
            ? new Date(r.row.payload.credentialFeeLastBilledAt).toLocaleString()
            : null;
        },
      },
      {
        field: 'Credential Fee Status',
        flex: 70,
        valueGetter: (r) => r.row.payload.credentialFeeState,
        renderCell: (r) =>
          r.row.payload.credentialFeeState ? r.row.payload.credentialFeeState : null,
      },
      {
        field: 'Actions',
        headerName: 'Actions',
        sortable: false,
        renderCell: (params) => renderActionCell(params),
        flex: 120,
        minWidth: 280,
        disableColumnMenu: true,
        filterable: false,
      },
    ];
  };
  const {
    createActions,
    onReject,
    setOnReject,
    onModify,
    setOnModify,
    currFeeReceiver,
    currUtilityFees,
    currBillingPeriodMinutes,
    currRate,
    currRewardReceiver,
    onChangeTopupAmount,
    setOnChangeTopupAmount,
    onRevoke,
    setOnRevoke,
  } = useCreateCommercialAgreementActions();
  const loadingContent = <CircularProgress sx={{ alignSelf: 'center' }} />;
  const loadedContent = (
    <>
      <CreateCommercialAgreementDialog open={open} onClose={onClose} />
      {!!onReject && (
        <RejectDialog
          open={!!onReject}
          onClose={() => setOnReject(undefined)}
          onReject={onReject}
        />
      )}
      {!!onModify && (
        <ModifyCommercialAgreementDialog
          currFeeReceiver={currFeeReceiver}
          currUtilityFees={currUtilityFees}
          currBillingPeriodMinutes={currBillingPeriodMinutes}
          currRate={currRate}
          currRewardReceiver={currRewardReceiver}
          onModify={onModify}
          open={!!onModify}
          onClose={() => setOnModify(undefined)}
        />
      )}

      <CommercialAgreementTopupDialog
        onChangeTopupAmount={onChangeTopupAmount}
        open={!!onChangeTopupAmount}
        onClose={() => setOnChangeTopupAmount(undefined)}
      />
      <CommercialAgreementRevokeDialog
        onRevoke={onRevoke}
        open={!!onRevoke}
        onClose={() => setOnRevoke(undefined)}
      />
      <DataTable columns={createHeaders(createActions)} rows={rows} rowKey={(a) => a.contractId} />
    </>
  );
  const displayedContent = isLoading ? loadingContent : loadedContent;
  return (
    <PageWrapper
      pageName="Commercial Agreements"
      buttonLabel="Create Commercial Agreement"
      onClick={isOperator ? onOpen : undefined}
    >
      {displayedContent}
    </PageWrapper>
  );
};
